import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { getAxiosInstance } from '../../utils/axiosInstance';
import { GRADIENTS, COLORS } from './themeConstants';
import { Toaster, toast } from 'react-hot-toast';

const fields = [
	{ name: 'position', label: 'Select your position', type: 'select', options: ['Entrepreneur', 'Student Entrepreneur'] },
	{ name: 'fullName', label: 'Full name', type: 'text' },
	{ name: 'phoneNumber', label: 'Phone number', type: 'tel' },
	{ name: 'email', label: 'Email address', type: 'email' },
	{ name: 'address', label: 'Address', type: 'text' },
	{ name: 'sslcPercent', label: 'SSLC mark percentage', type: 'text' },
	{ name: 'plusTwoPercent', label: 'Plus two mark percentage', type: 'text' },
	{
		name: 'degree',
		label: 'Degree',
		type: 'select',
		options: ['Bachelor of Computer Applications (B.C.A.)', 'Bachelor of Science in Computer Science (B.Sc. C.S.)', 'Bachelor of Technology (B.Tech.)', 'Master of Computer Applications (M.C.A.)', 'Master of Science in Computer Science (M.Sc. C.S.)', 'Master of Technology (M.Tech.)'],
	},
	{ name: 'degreePercent', label: 'Degree mark percentage', type: 'text' },
	{ name: 'institute', label: 'Institute where MERN course was completed', type: 'text' },
	{ name: 'linkedIn', label: 'Linked Id', type: 'text' },
];

function Career() {
	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const [showModal, setShowModal] = useState(false);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const validateForm = () => {
		const newErrors = {};
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const phoneRegex = /^[0-9]{10}$/;

		if (!formData.position) newErrors.position = 'select your position';
		if (!formData.fullName) newErrors.fullName = 'Full Name is required';
		if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone Number is required';
		else if (!phoneRegex.test(formData.phoneNumber)) newErrors.phoneNumber = 'Invalid phone number';
		if (!formData.email) newErrors.email = 'Email is required';
		else if (!emailRegex.test(formData.email)) newErrors.email = 'Invalid email';
		if (!formData.address) newErrors.address = 'Address is required';
		if (!formData.sslcPercent) newErrors.sslcPercent = 'SSLC percentage is required';
		if (!formData.plusTwoPercent) newErrors.plusTwoPercent = 'Plus Two percentage is required';
		if (!formData.degree) newErrors.degree = 'Choose degree is required';
		if (!formData.institute) newErrors.institute = 'Institute name is required';
		if (!formData.linkedIn) newErrors.linkedIn = 'LinkedIn is required';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (validateForm()) {
			setLoading(true);
			try {
				const payload = {
					position: formData.position,
					fullName: formData.fullName,
					phoneNumber: formData.phoneNumber,
					email: formData.email,
					address: formData.address,
					sslcPercent: formData.sslcPercent,
					plusTwoPercent: formData.plusTwoPercent,
					linkedIn: formData.linkedIn,
					degree: formData.degree,
					degreePercent: formData.degreePercent,
					institute: formData.institute,
				};

				payload.institute = formData.institute;

				const instance = getAxiosInstance();
				await instance.post('/website/career', payload);
				setShowModal(true);
				toast.success('Registration successful!');
				setTimeout(() => (window.location.href = '/'), 2000);
			} catch (error) {
				console.error(error);
				toast.error(error.response?.data?.message || 'Registration failed');
			} finally {
				setLoading(false);
			}
		}
	};

	const computedFields = fields.map((field) => {
		if (formData.position === 'Student Entrepreneur') {
			if (field.name === 'institute') {
				return { ...field, label: 'Name of current college' };
			}
			if (field.name === 'degreePercent') {
				return { ...field, label: 'Mark percentage of the current semester' };
			}
		}
		return field;
	});

	return (
		<div className={`flex-row bg-gradient-to-br ${GRADIENTS.background.primary} p-6 justify-items-center`}>
			<Toaster position='top-right' />

			<div className='text-center mb-6'>
				<h1 className='text-3xl font-semibold text-blue-900'>Career Registration</h1>
				<p className='text-lg text-gray-600 mt-2'>Please fill in the details below to register your career.</p>
			</div>

			<motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className={`w-full max-w-3xl  ${COLORS.background.glass} rounded-2xl shadow-xl p-8 backdrop-blur-lg border ${COLORS.border.glass}`}>
				<h2 className='text-2xl font-bold mb-6 text-blue-900'>Basic Details</h2>

				<div className='space-y-5 overflow-y-auto'>
					{computedFields.map((field) => (
						<div key={field.name}>
							<label className='block text-sm font-semibold text-gray-700 mb-1'>{field.label}</label>
							{field.type === 'select' ? (
								<select name={field.name} value={formData[field.name] || ''} onChange={handleInputChange} className={`w-full px-4 py-2 rounded-lg ${COLORS.background.glass} border ${COLORS.border.glass} focus:outline-none focus:ring-2 focus:ring-blue-500`}>
									<option value=''>Select an option</option>
									{field.options.map((option) => (
										<option key={option} value={option}>
											{option}
										</option>
									))}
								</select>
							) : (
								<input type={field.type} name={field.name} value={formData[field.name] || ''} onChange={handleInputChange} className={`w-full px-4 py-2 rounded-lg ${COLORS.background.glass} border ${COLORS.border.glass} focus:outline-none focus:ring-2 focus:ring-blue-500`} />
							)}
							{errors[field.name] && <p className='text-red-500 text-sm mt-1'>{errors[field.name]}</p>}
						</div>
					))}
				</div>

				<div className='flex justify-end mt-8'>
					<button onClick={handleSubmit} disabled={loading} className={`px-6 py-2 rounded-lg bg-gradient-to-r ${GRADIENTS.button.primary} text-white hover:opacity-90 transition duration-300`}>
						{loading ? <span className='inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-white' /> : 'Submit'}
					</button>
				</div>
			</motion.div>
			{showModal && (
	<div
		className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
		onClick={() => {
			setShowModal(false);
			window.location.href = '/';
		}}
	>
		<div
			className="bg-white p-8 rounded-xl shadow-lg max-w-xl h-60 text-center cursor-default"
			onClick={(e) => e.stopPropagation()}
		>
			<h2 className="text-2xl font-bold text-blue-900 mb-4">Thank you for visiting Necttos!</h2>
			<p className="text-gray-700 mb-4">We're excited to have you as part of our team.</p>
			<p className="text-gray-600">We will contact you soon.</p>
		</div>
	</div>
)}

		</div>
	);
}

export default Career;
